import React, { useEffect, useState } from 'react';
import { SaveAs } from '@mui/icons-material';
import { Button } from '@mui/material';
import { jsPDF as JSPDF } from 'jspdf';
import PropTypes from 'prop-types';
import 'jspdf-autotable';

import axiosInstance from '../../api';
import amharicFont from '../../utils/nyala.ttf'
import { ethDate } from '../../utils/ethDate';
import SERVER_API_URL from '../../config';

const DownloadPDFButton = ({ church, user }) => {
  const [avatar, setAvatar] = useState();

  useEffect(() => {
    const getAvatar = async () => {
      try {
        const response = await axiosInstance.get(
          `${SERVER_API_URL}/churchAvatar/getAvatarById/${church.id}`,
          {
            responseType: 'blob',
          }
        );
        if (response.status === 200) {
          const url = URL.createObjectURL(response.data);
          setAvatar(url);
        }
      } catch (e) {
        console.error('Failed to fetch avatar:', e);
        setAvatar(null);
      }
    };
    getAvatar();
  }, [church]);

  const generatePDF = () => {
    const pdf = new JSPDF();

    // Load the font
    pdf.addFileToVFS(amharicFont);
    pdf.addFont(amharicFont, 'AmharicFont', 'normal');

    // Set the font for the text
    pdf.setFont('AmharicFont');

    // Set header
    pdf.setFontSize(18);
    pdf.addImage("/assets/logo/eotc-logo.png", 'PNG', 8, 3, 10, 10);
    pdf.text(20, 10, "የሰሜን ሸዋ ሃገረ-ስብከት የመረጃ አስተዳደር ስርዓት");
    const currentDate = ethDate(new Date())
    pdf.setFontSize(12);
    pdf.text(currentDate, pdf.internal.pageSize.getWidth() - 40, 10);

    // (x1, y1, x2, y2)
    pdf.line(10, 15, 200, 15);

    // (image, format, x, y, width, height)
    if (avatar) {
      pdf.addImage(avatar, 'PNG', 10, 20, 120, 80);
    } else {
      pdf.addImage("/assets/landing/empty_church.png", 'PNG', 10, 20, 120, 80);
    }

    const xAttribute = 136;
    const xValue = 150;
    let y = 25;
    const increment = 6;

    pdf.text("የአጥቢያ ስም፡ ", xAttribute - 2, y)
    pdf.setFontSize(14);
    pdf.text(church.name, xValue + 6, y)

    pdf.setFontSize(12);

    y += increment + 2;
    pdf.text("አውራጃ: ", xAttribute + 2, y)
    pdf.text(church.district.name, xValue, y)

    y += increment;
    pdf.text("ዓይነት: ", xAttribute + 2, y)
    pdf.text(church.type, xValue, y)

    y += increment;
    pdf.text("ኢሜል: ", xAttribute + 2, y)
    pdf.text(church.email, xValue, y)

    y += increment;
    pdf.text("ስልክ: ", xAttribute + 4, y)
    pdf.text(church.phone, xValue, y)

    y += increment;
    pdf.text("ተጠቃሚ: ", xAttribute - 1, y)
    pdf.text(church.usersCount.toString(), xValue + 2, y)

    y += increment;
    pdf.text("ተመዘገበ: ", xAttribute, y)
    pdf.text(ethDate(church.createdAt), xValue, y)

    const descriptionLines = pdf.splitTextToSize(church.description, 180);
    let inc = 110
    descriptionLines.forEach((line) => {
      if (inc > 270) {
        pdf.addPage();
        inc = 20;
      }
      pdf.text(line, 10, inc);
      inc += 6;
    });

    pdf.setFontSize(14);
    pdf.text("የአጥቢያው አጭር ታሪክ ", 15, inc + 6);
    pdf.setFontSize(12)
    const historyLines = pdf.splitTextToSize(church.history, 180);
    inc += 14;
    historyLines.forEach((line) => {
      if (inc > 270) {
        pdf.addPage();
        inc = 20;
      }
      pdf.text(line, 10, inc);
      inc += 6;
    });

    pdf.setFontSize(14);
    inc += 10;
    pdf.text("የአጥቢያው ተከታዮች ዝርዝር", 15, inc);
    inc += 5;

    const userData = church.users.map((user, index) => [
      index + 1,
      `${user.firstName} ${user.lastName}`,
      user.christianName,
      user.phone,
      user.gender,
      // user.email,
      // user.role.name,
      user.category.name,
      `${user.confessor ? user.confessor.firstName : 'የለም'} ${user.confessor ? user.confessor.lastName : ''}`,
      ethDate(user.birthDate)
    ]);

    // head: [['ተ.ቁ', 'የአጥቢያ ስም', 'ዓይነት', 'ተጠቃሚዎች', 'ስልክ', 'ኢሜይል']],
    pdf.autoTable({
      startY: inc,
      head: [['ተ.ቁ', 'ሙሉ ስም', 'የክርስትና ስም', 'ስልክ ቁጥር', 'ጾታ', 'ምድብ', 'የንስሃ አባት', 'የትውልድ ዘመን']], // ,
      body: userData,
      theme: 'grid',
      styles: {
        font: 'AmharicFont',
        fontSize: 12,
      },
      columnStyles: {
        0: { columnWidth: 18 },  // 'ተ.ቁ'
        1: { columnWidth: 35 },  // 'ሙሉ ስም'
        2: { columnWidth: 25 },  // 'የክርስትና ስም'
        3: { columnWidth: 28 },  // 'ስልክ ቁጥር'
        4: { columnWidth: 15 },  // 'ጾታ'
        5: { columnWidth: 25 },  //  'ምድብ'
        6: { columnWidth: 25 },  //  'የንስሃ አባት'
        7: { columnWidth: 15 },  // 'የትውልድ ዘመን'
      },
    });

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i += 1) {
      pdf.setTextColor('gray');

      pdf.setPage(i);
      pdf.setFontSize(12);
      pdf.text(
        `ገጽ ${i} ክ ${totalPages}`,
        pdf.internal.pageSize.getWidth() - 40,
        pdf.internal.pageSize.getHeight() - 10
      );

      pdf.text(
        `አዘጋጅ፡ ${user && user.firstName} ${user && user.lastName} (${user && user.role.name})`,
        10,
        pdf.internal.pageSize.getHeight() - 10
      );
    }
    pdf.save(`${church && church.name} አጥቢያ.pdf`);
  };

  const handleClick = () => {
    generatePDF();
  };

  return (
    <Button onClick={handleClick}>
      <SaveAs />{' '}PDF አውርድ
    </Button>
  );
};

export default DownloadPDFButton;

DownloadPDFButton.propTypes = {
  church: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    district: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    type: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    usersCount: PropTypes.number.isRequired,
    createdAt: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    history: PropTypes.string.isRequired,
    users: PropTypes.arrayOf(PropTypes.shape({
      phone: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    christianName: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    confessor: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }),
    role: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    category: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    birthDate: PropTypes.string.isRequired,
  }).isRequired,
};
